import { Info, Loader2, RefreshCw } from 'lucide-react';
import React from 'react';
import { CollapsibleInfoBlock } from '../../components/CollapsibleInfoBlock';
import { JiraService } from '../../services/jira';
import { apiFetch } from '../../shared/apiClient';
import { useDatabaseUpdate } from '../../shared/hooks';
import { usePermission } from '../../shared/hooks/usePermission';

interface DatabaseUpdaterProps {
  jiraService: JiraService;
  onUpdateComplete?: () => void;
  /** Post–welcome-tour spotlight on Analytics */
  spotlightActive?: boolean;
  onSpotlightDismiss?: () => void;
}

export const DatabaseUpdater: React.FC<DatabaseUpdaterProps> = ({
  jiraService,
  onUpdateComplete,
  spotlightActive = false,
  onSpotlightDismiss,
}) => {
  const progressBarRef = React.useRef<HTMLDivElement>(null);
  const progressTextRef = React.useRef<HTMLDivElement>(null);
  const logsContainerRef = React.useRef<HTMLDivElement>(null);
  const [isUpdatingFixVersions, setIsUpdatingFixVersions] = React.useState(false);
  const [isIncrementalUpdate, setIsIncrementalUpdate] = React.useState(false);

  // Функция для прямого обновления прогресса в DOM
  const updateProgressDOM = React.useCallback((progressValue: number) => {
    if (progressBarRef.current) {
      progressBarRef.current.style.width = `${progressValue}%`;
    }

    if (progressTextRef.current) {
      progressTextRef.current.textContent = `${progressValue}%`;
    }
  }, []);

  const { isUpdating, logs, error, fullUpdate, addLog, clearLogs, progressMessage } =
    useDatabaseUpdate(jiraService, onUpdateComplete, updateProgressDOM);

  // Full Update is a potentially-destructive rebuild of the entire worklog
  // history — gated to owner/administrator. Members still see Incremental +
  // Update Fix Versions (see `worklog.incremental_refresh`).
  const { allowed: canFullRefresh } = usePermission('worklog.full_refresh');

  const handleFullUpdate = React.useCallback(() => {
    onSpotlightDismiss?.();
    fullUpdate();
  }, [fullUpdate, onSpotlightDismiss]);

  // Автоскролл логов вниз при появлении новых записей (живой лог)
  React.useEffect(() => {
    if (logsContainerRef.current && logs.length > 0) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [logs.length, logs]);

  const runIncrementalUpdate = React.useCallback(async () => {
    setIsIncrementalUpdate(true);
    updateProgressDOM(0);
    addLog('🚀 Incremental Update: fetching only issues updated since last Full Update...');
    addLog('⏳ Progress will update every batch. Do not close the page.');

    try {
      const response = await apiFetch('/api/worklog/incremental-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        const msg = errJson.message || errJson.error || response.statusText;
        throw new Error(msg);
      }

      if (!response.body) {
        throw new Error('Response body is not available');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.progress !== undefined) {
                updateProgressDOM(data.progress);
              }
              if (data.message) {
                addLog(data.message);
              }
              if (data.type === 'complete') {
                addLog(
                  `✅ Incremental Update complete! Updated ${data.updated ?? 0} tasks, ${data.totalWorklogs ?? 0} worklog rows`
                );
                if (onUpdateComplete) {
                  onUpdateComplete();
                }
              }
              if (data.type === 'error') {
                throw new Error(data.message || 'Unknown error');
              }
            } catch (e) {
              // ignore parse errors for non-JSON lines
            }
          }
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      addLog(`❌ Error: ${errorMessage}`);
      console.error('Incremental Update error:', error);
    } finally {
      setIsIncrementalUpdate(false);
      updateProgressDOM(0);
    }
  }, [addLog, updateProgressDOM, onUpdateComplete]);

  const updateFixVersions = React.useCallback(async () => {
    setIsUpdatingFixVersions(true);
    updateProgressDOM(0);
    addLog('🚀 Starting fixVersion update for existing tasks...');
    addLog('📋 This will only fetch fixVersion from Jira (much faster than Full Update)');

    try {
      const response = await apiFetch('/api/worklog/update-fix-versions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(
          `Failed to start fixVersion update: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      if (!response.body) {
        throw new Error('Response body is not available');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.progress !== undefined) {
                updateProgressDOM(data.progress);
              }
              if (data.message) {
                addLog(data.message);
              }
              if (data.type === 'complete') {
                addLog(`✅ Complete! Updated ${data.updated} tasks with fixVersion`);
                if (onUpdateComplete) {
                  onUpdateComplete();
                }
              }
              if (data.type === 'error') {
                throw new Error(data.error || 'Unknown error');
              }
            } catch (e) {
              // Игнорируем ошибки парсинга отдельных строк
            }
          }
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      addLog(`❌ Error: ${errorMessage}`);
      console.error('Error updating fixVersions:', error);
    } finally {
      setIsUpdatingFixVersions(false);
      updateProgressDOM(0);
    }
  }, [addLog, updateProgressDOM, onUpdateComplete]);

  const fullUpdateBusy = isUpdating || isUpdatingFixVersions || isIncrementalUpdate;
  const showProgressArea = fullUpdateBusy || logs.length > 0;
  const fullUpdateButton = (
    <button
      type="button"
      onClick={handleFullUpdate}
      disabled={fullUpdateBusy}
      className={`btn btn-primary${spotlightActive ? ' full-update-spotlight' : ''}`}
      aria-describedby={spotlightActive ? 'full-update-spotlight-desc' : undefined}
    >
      {isUpdating ? (
        <>
          <Loader2 size={16} className="animate-spin" />
          Updating...
        </>
      ) : (
        <>
          <RefreshCw size={16} />
          Full Update
        </>
      )}
    </button>
  );

  return (
    <div className="database-updater">
      <CollapsibleInfoBlock
        title="About Database Updater"
        defaultCollapsed={true}
        items={[
          'Full Update: loads all tasks from the first to the last in your Jira project (from My Profile). Use for first sync or full rebuild.',
          'Incremental Update: fetches only issues changed after the last Full Update. Run Full Update at least once before using.',
          'Update Fix Versions: refreshes only the Fix Version field for existing rows. Does not reload full worklog.',
        ]}
      />
      <div className="updater-header">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          Database Updater
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            <Info size={16} style={{ color: 'var(--text-gray)' }} />
          </span>
        </h3>
      </div>

      <div className="updater-controls">
        {canFullRefresh ? (
          spotlightActive ? (
            <div className="full-update-spotlight-wrap">
              <span id="full-update-spotlight-desc" className="sr-only">
                Recommended first step: sync Jira worklog data. Use the Full Update button below.
              </span>
              {fullUpdateButton}
            </div>
          ) : (
            fullUpdateButton
          )
        ) : null}

        <button
          onClick={runIncrementalUpdate}
          disabled={isUpdating || isUpdatingFixVersions || isIncrementalUpdate}
          className="btn btn-secondary"
        >
          {isIncrementalUpdate ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Updating...
            </>
          ) : (
            <>
              <RefreshCw size={16} />
              Incremental Update
            </>
          )}
        </button>

        <button
          onClick={updateFixVersions}
          disabled={isUpdating || isUpdatingFixVersions || isIncrementalUpdate}
          className="btn btn-secondary"
        >
          {isUpdatingFixVersions ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Updating...
            </>
          ) : (
            <>
              <RefreshCw size={16} />
              Update Fix Versions
            </>
          )}
        </button>
      </div>

      <div
        className="progress-section"
        style={{
          display: showProgressArea ? 'block' : 'none',
        }}
      >
        <div className="progress-bar">
          <div
            ref={progressBarRef}
            className={`progress-fill ${fullUpdateBusy ? 'progress-fill--pulse' : ''}`}
            style={{ width: '0%' }}
          />
        </div>
        <div ref={progressTextRef} className="progress-text">
          0%
        </div>
        {fullUpdateBusy ? (
          <p className="progress-hint">
            {progressMessage ||
              'Live progress — logs update in real time. Scroll down to see latest.'}
          </p>
        ) : logs.length > 0 ? (
          <p className="progress-hint">Last run finished. Logs stay here until you clear them.</p>
        ) : null}
      </div>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {logs.length > 0 && (
        <div className="logs-section">
          <div className="logs-section-header">
            <h4>Update Logs {fullUpdateBusy ? <span className="logs-live">● Live</span> : null}</h4>
            <button
              type="button"
              className="btn btn-secondary btn-sm logs-clear-btn"
              onClick={() => clearLogs()}
            >
              Clear logs
            </button>
          </div>
          <div ref={logsContainerRef} className="logs-container">
            {logs.map((log, index) => (
              <div key={index} className={`log-entry ${log.includes('Error') ? 'error' : ''}`}>
                {log}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseUpdater;
